import type { JSX } from "react";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { currentColors } from "../../colors";
import t from "../../translations";
import type { SelectInImageAnswerType } from "../../types";
import ImagePinIcon from "../icons/ImagePinIcon";

interface IProps {
    image: {
        mediaType: string;
        src: string;
    };
    pins: {
        part: number;
        clickX: number;
        clickY: number;
        answerType?: SelectInImageAnswerType | "undecided";
    }[];
}

const Container = styled.div<{
    $topMargin: number;
}>`
    position: relative;
    display: inline-block;
    user-select: none;
    margin-top: ${(props) => props.$topMargin}px;
`;

const StyledImage = styled.img`
    display: block;
    max-width: 100%;
    height: auto;
`;

const PinContainer = styled.div<{
    $left: number;
    $top: number;
}>`
    position: absolute;
    left: ${(props) => props.$left}px;
    top: ${(props) => props.$top}px;
    width: 30px;
    height: 60px;
    transform: translate(-50%, -100%);
    pointer-events: none;
`;

const StyledPin = styled(ImagePinIcon)`
    width: 100%;
    height: 100%;
`;

function SelectInImage({ image, pins }: IProps): JSX.Element {
    const imageRef = useRef<HTMLImageElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    const denormalizeCoordinates = useCallback((normalizedX: number, normalizedY: number) => {
        if (!imageRef.current) {
            return { x: 0, y: 0 };
        }

        const x = normalizedX * imageRef.current.offsetWidth;
        const y = normalizedY * imageRef.current.offsetHeight;

        return { x, y };
    }, []);

    // Calculate required top margin to prevent pins from sticking out above the image
    const topMargin = useMemo(() => {
        if (!imageLoaded || !imageRef.current || pins.length === 0) {
            return 0;
        }

        const PIN_HEIGHT = 60;
        let maxPinOverflow = 0;

        for (const pin of pins) {
            const { y } = denormalizeCoordinates(pin.clickX, pin.clickY);
            // Pin is positioned with transform: translate(-50%, -100%), so it extends PIN_HEIGHT pixels above its position
            const pinTop = y - PIN_HEIGHT;
            if (pinTop < 0) {
                maxPinOverflow = Math.max(maxPinOverflow, Math.abs(pinTop));
            }
        }

        return maxPinOverflow;
    }, [imageLoaded, pins, denormalizeCoordinates]);

    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const getPinColor = useCallback((answerType?: SelectInImageAnswerType | "undecided"): string => {
        switch (answerType) {
            case "correct":
                return currentColors.selectInImageCorrect;
            case "incorrect":
                return currentColors.selectInImageIncorrect;
            case "missed":
                return currentColors.selectInImage;
            default:
                return currentColors.selectInImage;
        }
    }, []);

    if (image.mediaType !== "Image") {
        return null;
    }

    return (
        <Container $topMargin={topMargin}>
            <StyledImage
                ref={imageRef}
                src={image.src}
                alt={t("shared.select-in-image.alt-select-in-image-image")}
                draggable={false}
                onLoad={handleImageLoad}
            />

            {imageLoaded &&
                pins.map((pin) => {
                    const { x, y } = denormalizeCoordinates(pin.clickX, pin.clickY);
                    const pinColor = getPinColor(pin.answerType);
                    return (
                        <PinContainer key={pin.part} $left={x} $top={y}>
                            <StyledPin number={pin.part} color={pinColor} />
                        </PinContainer>
                    );
                })}
        </Container>
    );
}

export default memo(SelectInImage);
